import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { 
  ArrowLeft, 
  Plus, 
  Trash2,
  Loader2,
  Users,
  Shield,
  User,
  Mail,
  Save,
  Building2
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "medewerker"
  });
  const [saving, setSaving] = useState(false);
  const { user: currentUser, getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: getAuthHeaders()
      });
      setUsers(response.data);
    } catch (error) {
      toast.error("Kon gebruikers niet laden");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setFormData({
      email: "",
      password: "",
      name: "",
      role: "medewerker"
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.email || !formData.password || !formData.name) {
      toast.error("Vul alle velden in");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/auth/register`, formData, {
        headers: getAuthHeaders()
      });
      toast.success("Gebruiker aangemaakt");
      setShowDialog(false);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Kon gebruiker niet aanmaken";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteUserId) return;

    try {
      await axios.delete(`${API}/users/${deleteUserId}`, {
        headers: getAuthHeaders()
      });
      toast.success("Gebruiker verwijderd");
      setDeleteUserId(null);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || "Kon gebruiker niet verwijderen";
      toast.error(message);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate("/")}
                className="text-white hover:bg-white/10"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-heading font-black text-xl">Gebruikers</h1>
                <p className="text-white/70 text-sm">Beheer medewerkers en admins</p>
              </div>
            </div>
            <Button
              onClick={handleAdd}
              className="bg-safety-orange hover:bg-safety-orange-dark"
              data-testid="add-user-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Toevoegen
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : users.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-muted-foreground">Nog geen gebruikers</p>
            </CardContent>
          </Card>
        ) : (
          <Card data-testid="users-list">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg">
                Alle Gebruikers ({users.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {users.map((user, index) => (
                  <div 
                    key={user.id} 
                    className="flex items-center gap-4 p-4"
                    data-testid={`user-item-${index}`}
                  >
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      user.role === 'admin' ? 'bg-primary/10' : (user.role === 'organisatie' || user.role === 'organisation') ? 'bg-blue-100' : 'bg-muted'
                    }`}>
                      {user.role === 'admin' ? (
                        <Shield className="w-6 h-6 text-primary" />
                      ) : (user.role === 'organisatie' || user.role === 'organisation') ? (
                        <Building2 className="w-6 h-6 text-blue-600" />
                      ) : (
                        <User className="w-6 h-6 text-muted-foreground" />
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold truncate">{user.name}</p>
                        <Badge variant={user.role === 'admin' ? 'default' : 'secondary'} className={
                          (user.role === 'organisatie' || user.role === 'organisation') ? 'bg-blue-500 text-white' : ''
                        }>
                          {user.role === 'admin' ? 'Admin' : (user.role === 'organisatie' || user.role === 'organisation') ? 'Organisatie' : 'Medewerker'}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {user.email}
                      </p>
                    </div>
                    
                    {user.id !== currentUser?.id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:bg-destructive/10"
                        onClick={() => setDeleteUserId(user.id)}
                        data-testid={`delete-user-${index}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Add User Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Nieuwe Gebruiker</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Naam</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Jan Jansen"
                data-testid="user-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="email">E-mailadres</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="jan@bedrijf.nl"
                data-testid="user-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Wachtwoord</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="••••••••"
                data-testid="user-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="role">Rol</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="medewerker">Medewerker</SelectItem>
                  <SelectItem value="organisatie">Organisatie</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {formData.role === "medewerker" && "Kan keuringen uitvoeren en indienen"}
                {formData.role === "organisatie" && "Kan alleen 'ter beoordeling' keuringen zien en beoordelen"}
                {formData.role === "admin" && "Volledige toegang inclusief gebruikers- en vraagbeheer"}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annuleren
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-primary"
              data-testid="save-user-button"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <Save className="w-4 h-4 mr-2" />
              Aanmaken
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteUserId} onOpenChange={() => setDeleteUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Gebruiker verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je deze gebruiker wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
              data-testid="confirm-delete-user-button"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminUsersPage;
